import React, { useMemo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { Box, Drawer, IconButton, Tooltip } from '@mui/material';
import BrandLogo from '@core/components/ui/BrandLogo';
import VerticalTabs, { TabItem } from '@core/components/ui/VerticalTabs';
import { getSidebarWidth } from '@core/constants/config';
import { useAppLayout } from '@core/contexts/LayoutContext';
import { useTheme } from '@core/contexts/ThemeContext';
import { NavItem } from '@core/interfaces';
import { getHomePath, matchesNavPath } from '@core/utils/routes';
import WebTwoNav from './WebTwoNav';

interface SidebarProps {
  open: boolean;
  collapsible?: boolean;
  onToggle: () => void;
  navItems: NavItem[];
  logoHeight: number;
  showLogo: boolean;
  width?: number;
  footer?: React.ReactNode;
}

const Sidebar: React.FC<SidebarProps> = ({
  open,
  collapsible = true,
  onToggle,
  navItems,
  logoHeight,
  showLogo,
  width,
  footer,
}) => {
  const { theme, mode } = useTheme();
  const { isMobile, isWebOne, isWebTwo } = useAppLayout();
  const location = useLocation();

  const isDarkMode = mode === 'dark';
  const showExpandedContent = !collapsible || isMobile || open;
  const drawerWidth = width ?? getSidebarWidth(showExpandedContent);

  const activeTab = useMemo(() => {
    return (
      navItems.find(
        (item) =>
          matchesNavPath(location.pathname, item.path) ||
          item.children?.some((c) => c.path === location.pathname)
      )?.path ?? ''
    );
  }, [navItems, location.pathname]);

  const tabItems = useMemo((): TabItem[] => {
    return navItems.map((item) => ({
      path: item.path,
      title: item.title,
      icon: item.icon,
      disabled: item.disabled,
    }));
  }, [navItems]);

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={open}
      onClose={isMobile ? onToggle : undefined}
      ModalProps={
        isMobile
          ? {
              sx: {
                pointerEvents: open ? 'auto' : 'none',
              },
            }
          : undefined
      }
      PaperProps={{
        elevation: 0,
        sx: {
          boxShadow: 'none',
          border: 'none',
          backgroundColor: isMobile
            ? 'background.default'
            : isWebTwo && isDarkMode
              ? 'grey.900'
              : 'transparent',
        },
      }}
      sx={{
        display: isMobile
          ? { xs: 'block', md: 'none' }
          : { xs: 'none', md: 'block' },
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: drawerWidth,
          borderRadius: 0,
          ...(!isMobile && {
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
          }),
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          bgcolor: 'transparent',
          borderRadius: 0,
          pl: isWebOne ? 2 : 1,
          pr: isWebOne ? 0 : 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: logoHeight,
            transition: isMobile ? undefined : 'opacity 0.5s ease-in-out',
          }}
        >
          {showLogo ? (
            <Box
              component={RouterLink}
              to={getHomePath(navItems)}
              aria-label="Go to dashboard"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <BrandLogo
                variant={showExpandedContent ? 'horizontal' : 'square'}
                height={logoHeight}
                width={showExpandedContent ? drawerWidth : logoHeight}
              />
            </Box>
          ) : null}
        </Box>

        <Box sx={{ flex: 1 }}>
          {isWebTwo ? (
            <WebTwoNav
              navItems={navItems}
              onItemClick={isMobile && open ? onToggle : undefined}
            />
          ) : (
            <VerticalTabs
              tabs={tabItems}
              activeTab={activeTab}
              collapsed={!showExpandedContent}
              onItemClick={isMobile && open ? onToggle : undefined}
            />
          )}
        </Box>

        {footer != null ? (
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              borderTop: isWebTwo ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            {footer}
          </Box>
        ) : collapsible ? (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Tooltip
              title={
                isMobile
                  ? 'Close sidebar'
                  : open
                    ? 'Collapse sidebar'
                    : 'Expand sidebar'
              }
              placement="right"
            >
              <IconButton
                onClick={onToggle}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                {showExpandedContent ? (
                  <ChevronLeftIcon />
                ) : (
                  <ChevronRightIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
